import { useState } from "react";
import { ChevronRight, UserCheck, UserPlus } from "lucide-react";
import { Card, PrimaryButton } from "./parts";

// A checklist belongs to the COMPANY. These two sheets are the whole vendor-side handoff: the
// office says who is doing each list, and whoever holds one can hand it back. No login, no
// console, no managerial controls — one card and nothing else.

export const OfficeLists = ({ t, lists = [], onPick }) => (
  <Card className="mt-4" data-testid="work-office-lists">
    <div className="text-[10.5px] font-bold uppercase tracking-wide text-ink/35">{t.holdTitle}</div>
    <p className="mt-1 text-[12px] leading-relaxed text-ink/45">{t.officeIntro}</p>
    <div className="mt-2.5 space-y-1.5">
      {lists.map((l) => (
        <button key={l.id} onClick={() => onPick(l)} data-testid={`work-list-${l.id}`}
          className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left ${
            l.holder ? "bg-sand-50" : "bg-amber-50"}`}>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14px] font-semibold text-ink">{l.name}</span>
            <span className={`block truncate text-[11.5px] ${l.holder ? "text-ink/45" : "font-semibold text-amber-800"}`}>
              {l.holder ? `${l.community} · ${l.holder}` : `${l.community} · ${t.nobodyOnIt}`}
            </span>
          </span>
          <span className="shrink-0 text-[11.5px] font-semibold text-redline-600">
            {l.holder ? t.change : t.pickSomeone}
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-sand-400" />
        </button>
      ))}
    </div>
  </Card>
);

export const AssignSheet = ({ t, list, crew = [], me, busy, onClose, onAssign }) => {
  const [mode, setMode] = useState("");
  const [pick, setPick] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  // Opened from "can't do it today" rather than the office card: one day's cover, so the sheet
  // drops "I'll do it myself" (they already have it) and leads with just today.
  const cover = !!list.visit_id;
  const chosen = mode === "me" ? { me: true } : mode === "new"
    ? { name: name.trim(), phone: phone.trim() } : pick ? { contact_id: pick } : null;
  const ready = mode === "me" || (mode === "new" ? !!(name.trim() && phone.trim()) : !!pick);
  const send = (scope) => onAssign({ template_id: list.id, scope, visit_id: list.visit_id || "", ...chosen });
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 px-4 pb-6" onClick={onClose}>
      <div className="max-h-[88vh] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-5"
        onClick={(e) => e.stopPropagation()} data-testid="work-assign-sheet">
        <div className="font-heading text-[16px] font-bold text-ink">{cover ? t.coverTitle : t.holdTitle}</div>
        <div className="mt-0.5 text-[12.5px] text-ink/45">{list.name} · {list.community}</div>
        {cover && <p className="mt-2 text-[12.5px] text-ink/55">{t.coverSub}</p>}

        {!cover && (
          <button onClick={() => { setMode("me"); setPick(""); }} data-testid="work-assign-me"
            className={`mt-3 flex w-full items-center gap-2.5 rounded-xl px-3.5 py-3 text-left text-[14px] font-semibold ${
              mode === "me" ? "bg-emerald-50 text-emerald-800" : "bg-sand-50 text-ink"}`}>
            <UserCheck className="h-4 w-4 shrink-0" /> {t.meDoIt}
          </button>
        )}

        {crew.filter((c) => c.id !== me).length > 0 && (
          <>
            <div className="mt-3 text-[10.5px] font-bold uppercase tracking-wide text-ink/35">{t.handTo}</div>
            <div className="mt-1.5 space-y-1.5">
              {crew.filter((c) => c.id !== me).map((c) => (
                <button key={c.id} onClick={() => { setMode("crew"); setPick(c.id); }}
                  data-testid={`work-assign-crew-${c.id}`}
                  className={`flex w-full items-center rounded-xl px-3.5 py-2.5 text-left text-[14px] font-semibold ${
                    pick === c.id ? "bg-emerald-50 text-emerald-800" : "bg-sand-50 text-ink"}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </>
        )}

        <button onClick={() => { setMode("new"); setPick(""); }} data-testid="work-assign-new"
          className={`mt-2 flex w-full items-center gap-2.5 rounded-xl px-3.5 py-3 text-left text-[14px] font-semibold ${
            mode === "new" ? "bg-emerald-50 text-emerald-800" : "bg-sand-50 text-ink"}`}>
          <UserPlus className="h-4 w-4 shrink-0" /> {t.newPerson}
        </button>
        {mode === "new" && (
          <div className="mt-2 space-y-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t.typeName}
              data-testid="work-assign-name"
              className="w-full rounded-xl border border-sand-300 px-3.5 py-3 text-[14px] outline-none focus:border-redline-400" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t.theirMobile}
              data-testid="work-assign-phone" inputMode="tel"
              className="w-full rounded-xl border border-sand-300 px-3.5 py-3 text-[14px] outline-none focus:border-redline-400" />
          </div>
        )}

        {!cover && <p className="mt-4 text-[12px] text-ink/45">{t.howLong}</p>}
        <div className="mt-3 flex gap-2">
          <button data-testid="work-assign-visit" disabled={busy || !ready} onClick={() => send("visit")}
            className={`flex-1 rounded-2xl px-4 py-3 text-[14px] font-semibold disabled:opacity-40 ${
              cover ? "bg-ink text-white" : "border border-sand-300 text-ink"}`}>
            {cover ? t.justToday : t.justNext}
          </button>
          <button data-testid="work-assign-always" disabled={busy || !ready} onClick={() => send("always")}
            className={`flex-1 rounded-2xl px-4 py-3 text-[14px] font-semibold disabled:opacity-40 ${
              cover ? "border border-sand-300 text-ink" : "bg-ink text-white"}`}>
            {t.fromNowOn}
          </button>
        </div>
      </div>
    </div>
  );
};

export const StepOffSheet = ({ t, lists = [], busy, onClose, onSend }) => {
  const [scope, setScope] = useState("");
  const [which, setWhich] = useState(lists.length === 1 ? lists[0].id : "");
  const [reason, setReason] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const ready = scope === "company" || (scope === "list" && !!which);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 px-4 pb-6" onClick={onClose}>
      <div className="max-h-[88vh] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-5"
        onClick={(e) => e.stopPropagation()} data-testid="work-stepoff-sheet">
        <div className="font-heading text-[16px] font-bold text-ink">{t.offTitle}</div>
        <p className="mt-1 text-[12.5px] leading-relaxed text-ink/45">{t.offNote}</p>

        <div className="mt-3 space-y-1.5">
          <button onClick={() => setScope("list")} data-testid="work-stepoff-list"
            className={`w-full rounded-xl px-3.5 py-3 text-left text-[14px] font-semibold ${
              scope === "list" ? "bg-emerald-50 text-emerald-800" : "bg-sand-50 text-ink"}`}>
            {t.offList}
          </button>
          <button onClick={() => setScope("company")} data-testid="work-stepoff-company"
            className={`w-full rounded-xl px-3.5 py-3 text-left text-[14px] font-semibold ${
              scope === "company" ? "bg-emerald-50 text-emerald-800" : "bg-sand-50 text-ink"}`}>
            {t.offLeft}
          </button>
        </div>

        {scope === "list" && lists.length === 0 && (
          <p className="mt-3 text-[12.5px] leading-relaxed text-amber-800" data-testid="work-stepoff-none">
            {t.offNoneHeld}
          </p>
        )}

        {scope === "list" && lists.length > 1 && (
          <>
            <div className="mt-3 text-[10.5px] font-bold uppercase tracking-wide text-ink/35">{t.offWhich}</div>
            <div className="mt-1.5 space-y-1.5">
              {lists.map((l) => (
                <button key={l.id} onClick={() => setWhich(l.id)} data-testid={`work-stepoff-which-${l.id}`}
                  className={`w-full rounded-xl px-3.5 py-2.5 text-left text-[13.5px] font-semibold ${
                    which === l.id ? "bg-emerald-50 text-emerald-800" : "bg-sand-50 text-ink"}`}>
                  {l.name} <span className="font-normal text-ink/45">· {l.community}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {scope && (
          <>
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t.offReasonPh}
              data-testid="work-stepoff-reason"
              className="mt-3 w-full resize-none rounded-xl border border-sand-300 px-3.5 py-3 text-[14px] outline-none focus:border-redline-400" />
            <div className="mt-2 text-[10.5px] font-bold uppercase tracking-wide text-ink/35">{t.offWho}</div>
            <div className="mt-1.5 space-y-2">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t.typeName}
                data-testid="work-stepoff-name"
                className="w-full rounded-xl border border-sand-300 px-3.5 py-3 text-[14px] outline-none focus:border-redline-400" />
              {name.trim() && (
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t.theirMobile}
                  data-testid="work-stepoff-phone" inputMode="tel"
                  className="w-full rounded-xl border border-sand-300 px-3.5 py-3 text-[14px] outline-none focus:border-redline-400" />
              )}
            </div>
            <PrimaryButton testid="work-stepoff-send" disabled={busy || !ready || (!!name.trim() && !phone.trim())}
              onClick={() => onSend({ scope, template_id: which, reason: reason.trim(),
                name: name.trim(), phone: phone.trim() })}>
              {t.offSend}
            </PrimaryButton>
          </>
        )}
      </div>
    </div>
  );
};
